import {useState} from 'react';
import {Cpu} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {type RdEvaluation} from '@/api/items';
import {useRdEvaluationHistory} from '@/api/hooks';
import {formatRdFinding, formatRdSignalName, isRiskSignal} from '@/lib/rdSignals';

type SignalView = {
  readonly name: string;
  readonly value: string | null;
};

// Text only: strings verbatim, everything structured as indented JSON. Rendered as React text nodes.
export function formatRdValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value === undefined) {
    return '';
  }

  return JSON.stringify(value, null, 2) ?? String(value);
}

// Unknown evaluator shapes still render; technical details retain the complete original signal.
export function describeSignal(signal: unknown): SignalView {
  if (typeof signal !== 'object' || signal === null || Array.isArray(signal)) {
    return {name: 'Unnamed signal', value: formatRdValue(signal)};
  }

  const {key, name, value} = signal as Record<string, unknown>;
  const keyLabel = typeof key === 'string' && key !== '';
  const nameLabel = !keyLabel && typeof name === 'string' && name !== '';

  return {
    name: keyLabel ? key : nameLabel ? name : 'Unnamed signal',
    value: 'value' in signal ? formatRdValue(value) : null
  };
}

const formatTime = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

function SignalValue({value, detailed}: {readonly value: unknown; readonly detailed: boolean}) {
  if (Array.isArray(value) && value.length > 0) {
    return (
      <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
        {value.map((entry: unknown, index) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed evaluator values have no identity
          <li key={index} className="bg-muted max-w-full rounded px-1.5 py-0.5">
            <SignalValue value={entry} detailed={detailed} />
          </li>
        ))}
      </ul>
    );
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    if (!detailed) {
      return <span className="text-muted-foreground">Structured value — see sidebar</span>;
    }

    return (
      <details>
        <summary className="cursor-pointer">View JSON</summary>
        <pre className="m-0 mt-1 whitespace-pre-wrap">{formatRdValue(value)}</pre>
      </details>
    );
  }

  return (
    <span className="whitespace-pre-wrap">
      {value === true || value === 'YES'
        ? 'Yes'
        : value === false || value === 'NO'
          ? 'No'
          : formatRdValue(value)}
    </span>
  );
}

function SignalRow({signal, detailed}: {readonly signal: unknown; readonly detailed: boolean}) {
  const view = describeSignal(signal);
  const value =
    typeof signal === 'object' && signal !== null && !Array.isArray(signal)
      ? (signal as Record<string, unknown>).value
      : signal;
  const provenance =
    typeof signal === 'object' && signal !== null && 'provenance' in signal
      ? (signal.provenance as {metadata?: {justification?: unknown; modality?: unknown}} | null)
      : null;
  const metadata = provenance?.metadata;
  return (
    <tr className="border-border border-b last:border-0">
      <th scope="row" className="w-2/5 py-1.5 pr-2 text-left align-top font-medium">
        {formatRdSignalName(view.name, true)}
      </th>
      <td className="py-1.5 align-top">
        {view.value === null ? (
          <span className="text-muted-foreground">No value supplied</span>
        ) : (
          <SignalValue value={value} detailed={detailed} />
        )}
        {detailed && typeof metadata?.justification === 'string' ? (
          <p className="m-0 mt-1 whitespace-pre-wrap">{metadata.justification}</p>
        ) : null}
        {detailed && typeof metadata?.modality === 'string' ? (
          <p className="text-muted-foreground m-0 mt-1">Assessed from: {metadata.modality}</p>
        ) : null}
        {detailed ? (
          <details className="text-muted-foreground mt-1">
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="m-0 mt-1 whitespace-pre-wrap">{formatRdValue(signal)}</pre>
          </details>
        ) : null}
      </td>
    </tr>
  );
}

function SignalTable({
  signals,
  detailed = false
}: {
  readonly signals: unknown[];
  readonly detailed?: boolean;
}) {
  return (
    <table aria-label="Signals" className="w-full table-fixed text-xs [overflow-wrap:anywhere]">
      <thead className="text-muted-foreground">
        <tr className="border-border border-b">
          <th scope="col" className="w-2/5 pr-2 pb-1 text-left font-medium">
            Signal
          </th>
          <th scope="col" className="pb-1 text-left font-medium">
            Value
          </th>
        </tr>
      </thead>
      <tbody>
        {signals.map((signal, index) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed list; signals have no identity
          <SignalRow key={index} signal={signal} detailed={detailed} />
        ))}
      </tbody>
    </table>
  );
}

function EvaluationView({
  evaluation,
  showSignals = true
}: {
  readonly evaluation: RdEvaluation;
  readonly showSignals?: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground m-0 text-xs break-words">
        Evaluated {formatTime(evaluation.evaluated_at)} · Evaluation {evaluation.evaluation_id}
      </p>
      {showSignals ? (
        evaluation.signals.filter(isRiskSignal).length === 0 ? (
          <p className="text-muted-foreground m-0 text-xs">Assessed — no risk signals emitted</p>
        ) : (
          <SignalTable detailed signals={evaluation.signals.filter(isRiskSignal)} />
        )
      ) : null}
      {evaluation.decisions.length === 0 ? (
        <p className="text-muted-foreground m-0 text-xs">No rule decisions recorded</p>
      ) : (
        <div>
          <p className="text-foreground m-0 text-xs font-medium">Rule decisions</p>
          <ul className="m-0 mt-1 list-none space-y-1.5 p-0" aria-label="Rule decisions">
            {evaluation.decisions.map((decision, index) => (
              // eslint-disable-next-line react/no-array-index-key -- fixed list; decisions have no identity
              <li key={index} className="border-border rounded-md border p-2">
                <pre className="text-muted-foreground m-0 font-mono text-[11px] break-words whitespace-pre-wrap">
                  {formatRdValue(decision)}
                </pre>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EvaluationHistory({
  jobId,
  itemId,
  currentId
}: {
  readonly jobId: string;
  readonly itemId: string;
  readonly currentId: string;
}) {
  const [open, setOpen] = useState(false);
  const history = useRdEvaluationHistory(jobId, itemId, open);
  const evaluations = history.data?.pages.flatMap((page) => page.results) ?? [];
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        render={
          <Button type="button" variant="ghost" size="xs" className="text-muted-foreground -ml-2" />
        }>
        {open ? 'Hide evaluation history' : 'Evaluation history'}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3" aria-live="polite">
          {history.isPending ? <Spinner aria-label="Loading evaluation history" /> : null}
          {history.isError ? (
            <p role="alert" className="text-destructive m-0 text-xs">
              Could not load evaluation history.
            </p>
          ) : null}
          {evaluations.map((evaluation) => (
            <div key={evaluation.evaluation_id} className="border-border rounded-md border p-2">
              {evaluation.evaluation_id === currentId ? (
                <Badge variant="tag" className="mb-1">
                  Current
                </Badge>
              ) : null}
              <EvaluationView evaluation={evaluation} />
            </div>
          ))}
          {history.hasNextPage ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={history.isFetchingNextPage}
              onClick={() => {
                void history.fetchNextPage();
              }}>
              Load older evaluations
            </Button>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// `assessment` is undefined where the list route doesn't report RD (the section then isn't shown),
// null when the item has no recorded evaluation.
export function RdSignalsSection({
  itemId,
  assessment,
  detailed = false
}: {
  readonly itemId: string;
  readonly assessment: RdEvaluation | null | undefined;
  readonly detailed?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (assessment === undefined) {
    return null;
  }

  const heading = (
    <p className="text-foreground m-0 flex items-center gap-1 text-xs font-medium">
      <Cpu aria-hidden="true" className="size-3 shrink-0" />
      Automated risk signals
    </p>
  );
  if (assessment === null) {
    return (
      <section aria-label="Automated RD signals" className="space-y-1">
        {heading}
        <p className="text-muted-foreground m-0 text-xs">No assessment available</p>
      </section>
    );
  }

  const tags = assessment.signals.flatMap((signal) => {
    if (typeof signal !== 'object' || signal === null || Array.isArray(signal)) {
      return [];
    }

    const {key, value} = signal as Record<string, unknown>;
    const {name} = describeSignal(signal);
    if (value === true || value === 'YES') {
      return [formatRdSignalName(name, true)];
    }

    const label = formatRdSignalName(name, true);
    if (
      typeof value === 'string' &&
      value.trim() !== '' &&
      value !== 'NO' &&
      typeof key === 'string' &&
      /^risk:[^:]+$/.test(key)
    ) {
      return [formatRdFinding(name, value)];
    }

    return typeof value === 'number' ? [`${label} · ${value}`] : [];
  });
  return (
    <section aria-label="Automated RD signals" className="space-y-1.5">
      {heading}
      {tags.length > 0 ? (
        <div aria-label="Positive signals and scores" className="flex flex-wrap gap-1">
          {tags.map((tag, index) => (
            <Badge
              // eslint-disable-next-line react/no-array-index-key -- fixed list; signals have no identity
              key={index}
              variant="outline"
              className="bg-muted h-auto max-w-full justify-start rounded-none text-left break-words whitespace-normal">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
      {assessment.signals.filter(isRiskSignal).length === 0 ? (
        <p className="text-muted-foreground m-0 text-xs">Assessed — no risk signals emitted</p>
      ) : detailed ? (
        <SignalTable detailed signals={assessment.signals.filter(isRiskSignal)} />
      ) : null}
      {detailed ? (
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="text-muted-foreground -ml-2"
              />
            }>
            {expanded ? 'Hide signal details' : 'Signal details'}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-2">
              <EvaluationView evaluation={assessment} showSignals={false} />
              <EvaluationHistory
                jobId={assessment.job_id}
                itemId={itemId}
                currentId={assessment.evaluation_id}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </section>
  );
}
